import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { COLORS } from "../../lib/brand";
import { Check, Plus, Pencil, Trash2, X, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";

const FREQUENCY_OPTIONS = [
  { value: "off", label: "Off" },
  { value: "daily_morning", label: "Daily — 9:00 AM" },
  { value: "daily_evening", label: "Daily — 6:00 PM" },
  { value: "weekly_monday", label: "Weekly — Monday 9 AM" },
  { value: "monthly_first", label: "Monthly — 1st of month" },
  { value: "monthly_fifteenth", label: "Monthly — 15th of month" },
];

const FREQ_SHORT = {
  off: "Off",
  daily_morning: "Daily 9 AM",
  daily_evening: "Daily 6 PM",
  weekly_monday: "Mon 9 AM",
  monthly_first: "Monthly · 1st",
  monthly_fifteenth: "Monthly · 15th",
};

export default function AdminTasks() {
  const [date, setDate] = useState("");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/tasks");
      setDate(r.data.date);
      setTasks(r.data.tasks || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggle(taskId, currentlyChecked) {
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, checked: !currentlyChecked } : t)));
    try {
      await api.post("/admin/tasks/check", { task_id: taskId, checked: !currentlyChecked });
    } catch (e) {
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, checked: currentlyChecked } : t)));
      toast.error(formatApiError(e));
    }
  }

  async function createTask(payload) {
    try {
      await api.post("/admin/tasks", payload);
      toast.success("Task added");
      setCreating(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function updateTask(id, payload) {
    try {
      await api.patch(`/admin/tasks/${id}`, payload);
      toast.success("Task updated");
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function deleteTask(id) {
    if (!confirm("Delete this task? Check history for this task will be cleared.")) return;
    try {
      await api.delete(`/admin/tasks/${id}`);
      toast.success("Task deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const daily = tasks.filter((t) => t.category === "daily");
  const weekly = tasks.filter((t) => t.category === "weekly");
  const completed = tasks.filter((t) => t.checked).length;
  const total = tasks.length;

  return (
    <div className="space-y-6" data-testid="admin-tasks-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-[#1A1A1A]">Task Tracker</h1>
          <p className="text-sm text-[#5C5C5C] mt-1">Daily admin + weekly CEO checklist · {date}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-[#5C5C5C]">
            <span className="text-2xl font-semibold text-[#800000]">{completed}</span> / {total} complete
          </div>
          <button
            onClick={() => setCreating(true)}
            className="text-sm text-white px-3 py-2 rounded-md flex items-center gap-1"
            style={{ background: COLORS.maroon }}
            data-testid="add-task-btn"
          >
            <Plus size={14} /> Add Task
          </button>
        </div>
      </header>

      {creating && (
        <TaskForm
          onSubmit={createTask}
          onCancel={() => setCreating(false)}
          submitLabel="Create Task"
        />
      )}

      {loading ? (
        <div className="text-sm text-[#5C5C5C]">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TaskSection
            title="Daily Admin"
            tasks={daily}
            onToggle={toggle}
            onDelete={deleteTask}
            onUpdate={updateTask}
            editingId={editingId}
            setEditingId={setEditingId}
            testid="daily-tasks"
          />
          <TaskSection
            title="Weekly CEO"
            tasks={weekly}
            onToggle={toggle}
            onDelete={deleteTask}
            onUpdate={updateTask}
            editingId={editingId}
            setEditingId={setEditingId}
            testid="weekly-tasks"
          />
        </div>
      )}

      <div className="bg-white border border-[#E8E2D5] rounded-xl p-5 text-sm text-[#5C5C5C]">
        <strong className="text-[#1A1A1A]">Automation:</strong> tasks with a reminder schedule will trigger an email/SMS via your GoHighLevel webhook when their cadence fires and the task is still unchecked.
      </div>
    </div>
  );
}

function TaskForm({ initial, onSubmit, onCancel, submitLabel = "Save" }) {
  const [form, setForm] = useState({
    category: initial?.category || "daily",
    title: initial?.title || "",
    auto_remind: initial?.auto_remind ?? false,
    reminder_frequency: initial?.reminder_frequency || "off",
  });
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    await onSubmit(form);
    setBusy(false);
  }

  return (
    <form
      onSubmit={submit}
      className="bg-white rounded-xl border border-[#800000] shadow-sm p-5 space-y-3"
      data-testid="task-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <select
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="px-3 py-2 border border-[#E8E2D5] rounded-md text-sm"
          data-testid="task-category"
        >
          <option value="daily">Daily Admin</option>
          <option value="weekly">Weekly CEO</option>
        </select>
        <input
          required
          placeholder="Task description"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="md:col-span-3 px-3 py-2 border border-[#E8E2D5] rounded-md text-sm"
          data-testid="task-title"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={form.auto_remind}
            onChange={(e) => setForm({ ...form, auto_remind: e.target.checked })}
            className="accent-[#800000]"
            data-testid="task-auto-remind"
          />
          <span className="text-[#1A1A1A]">Send reminder if unchecked</span>
        </label>
        <select
          value={form.reminder_frequency}
          onChange={(e) => setForm({ ...form, reminder_frequency: e.target.value })}
          className="md:col-span-2 px-3 py-2 border border-[#E8E2D5] rounded-md text-sm"
          disabled={!form.auto_remind}
          data-testid="task-frequency"
        >
          {FREQUENCY_OPTIONS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy}
          className="px-4 py-2 rounded-md text-sm text-white"
          style={{ background: COLORS.maroon }}
          data-testid="task-submit"
        >
          {busy ? "Saving..." : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-md text-sm text-[#5C5C5C] hover:text-[#800000]"
          data-testid="task-cancel"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function TaskSection({ title, tasks, onToggle, onDelete, onUpdate, editingId, setEditingId, testid }) {
  return (
    <section className="bg-white rounded-xl border border-[#E8E2D5] shadow-sm" data-testid={testid}>
      <div className="px-5 py-4 border-b border-[#E8E2D5]">
        <h2 className="font-medium text-[#1A1A1A]">{title}</h2>
      </div>
      <ul className="divide-y divide-[#E8E2D5]">
        {tasks.length === 0 ? (
          <li className="px-5 py-4 text-sm text-[#5C5C5C]">No tasks in this section.</li>
        ) : (
          tasks.map((t) =>
            editingId === t.id ? (
              <li key={t.id} className="px-5 py-3.5">
                <TaskForm
                  initial={t}
                  onSubmit={(payload) => onUpdate(t.id, payload)}
                  onCancel={() => setEditingId(null)}
                  submitLabel="Save Changes"
                />
              </li>
            ) : (
              <li key={t.id} className="px-5 py-3.5 flex items-start gap-3 group">
                <button
                  onClick={() => onToggle(t.id, t.checked)}
                  className={`shrink-0 mt-0.5 w-6 h-6 rounded-md border-2 flex items-center justify-center transition ${
                    t.checked ? "border-[#800000] bg-[#800000]" : "border-[#E8E2D5] bg-white hover:border-[#800000]"
                  }`}
                  data-testid={`task-toggle-${t.id}`}
                  aria-label={t.checked ? "Uncheck" : "Check"}
                >
                  {t.checked && <Check size={14} color="#fff" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm ${t.checked ? "line-through text-[#5C5C5C]" : "text-[#1A1A1A]"}`}>
                    {t.title}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {t.auto_remind ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#FBF8F1", color: COLORS.gold }}>
                        <Bell size={10} /> {FREQ_SHORT[t.reminder_frequency] || "Reminder on"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#5C5C5C]">
                        <BellOff size={10} /> No reminder
                      </span>
                    )}
                    {t.is_default && (
                      <span className="text-[10px] uppercase tracking-wider text-[#5C5C5C]">Default</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button
                    onClick={() => setEditingId(t.id)}
                    className="p-1.5 text-[#5C5C5C] hover:text-[#800000]"
                    title="Edit"
                    data-testid={`task-edit-${t.id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => onDelete(t.id)}
                    className="p-1.5 text-[#5C5C5C] hover:text-[#800000]"
                    title="Delete"
                    data-testid={`task-delete-${t.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            )
          )
        )}
      </ul>
    </section>
  );
}
